import React from 'react';
import {
  Box,
  Button,
  Flex,
  FormControl,
  Input,
  Text,
  Select,
  FormLabel,
  VStack,
} from '@chakra-ui/react';
import { countryOptions } from 'types';
import { InfluencerLocation } from '@foodfluence/shared';
import LocationsListWithForm from '../../profile/components/LocationsListWithForm';
import { DisplayHeading } from '../../../components/ui';
import { ff } from '../../../theme';

interface ApplicationFormStepProps {
  email: string;
  setEmail: (value: string) => void;
  firstName: string;
  setFirstName: (value: string) => void;
  lastName: string;
  setLastName: (value: string) => void;
  instagram: string;
  setInstagram: (value: string) => void;
  tiktok: string;
  setTiktok: (value: string) => void;
  phoneNumber: string;
  setPhoneNumber: (value: string) => void;
  confirmPhoneNumber: string;
  setConfirmPhoneNumber: (value: string) => void;
  countryCode: string;
  setCountryCode: (value: string) => void;
  phoneError: string;
  locations: InfluencerLocation[];
  setLocations: (value: InfluencerLocation[]) => void;
  isLoading: boolean;
  onSubmit: () => void;
}

const ApplicationFormStep: React.FC<ApplicationFormStepProps> = ({
  email,
  setEmail,
  firstName,
  setFirstName,
  lastName,
  setLastName,
  instagram,
  setInstagram,
  tiktok,
  setTiktok,
  phoneNumber,
  setPhoneNumber,
  confirmPhoneNumber,
  setConfirmPhoneNumber,
  countryCode,
  setCountryCode,
  phoneError,
  locations,
  setLocations,
  isLoading,
  onSubmit,
}) => {
  const RequiredStar = () => <Text as="span" color={ff.danger} ml={1}>*</Text>;

  return (
    <>
      <VStack spacing={2} align="stretch" pb={2}>
        <Text textStyle="eyebrow" color={ff.text2}>Creator application</Text>
        <DisplayHeading size="32px">Apply to join Foodfluence as a creator</DisplayHeading>
      </VStack>

      <FormControl isRequired>
        <FormLabel>Email</FormLabel>
        <Input
          type="email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          placeholder="Email"
        />
      </FormControl>

      <FormControl isRequired>
        <FormLabel>First name</FormLabel>
        <Input
          type="text"
          value={firstName}
          onChange={e => setFirstName(e.target.value)}
          placeholder="First name"
        />
      </FormControl>

      <FormControl isRequired>
        <FormLabel>Last name</FormLabel>
        <Input
          type="text"
          value={lastName}
          onChange={e => setLastName(e.target.value)}
          placeholder="Last name"
        />
      </FormControl>

      <Box width="100%" p={5} border="1px solid" borderColor={ff.line} borderRadius="20px">
        <Text fontWeight={600} fontSize="17px" color={ff.ink} mb={1}>
          Social media accounts<RequiredStar />
        </Text>
        <Text fontSize="sm" color={ff.text2} mb={4}>
          Please provide at least one social media account (Instagram and/or TikTok)
        </Text>

        <FormControl mb={4}>
          <FormLabel>Instagram handle</FormLabel>
          <Input
            type="text"
            value={`@${instagram}`}
            onChange={e => setInstagram(e.target.value.replace('@', '').toLowerCase())}
            placeholder="@username"
          />
        </FormControl>

        <FormControl>
          <FormLabel>TikTok handle</FormLabel>
          <Input
            type="text"
            value={`@${tiktok}`}
            onChange={e => setTiktok(e.target.value.replace('@', '').toLowerCase())}
            placeholder="@username"
          />
        </FormControl>
      </Box>

      <FormControl isRequired>
        <FormLabel>Phone number</FormLabel>
        <Flex gap={2}>
          <Select
            value={countryCode}
            onChange={(e) => setCountryCode(e.target.value)}
            width="31%"
            aria-label="Country code"
          >
            {countryOptions.map(option => (
              <option key={option.code} value={option.code}>
                {option.code}
              </option>
            ))}
          </Select>
          <Input
            type="tel"
            placeholder="Phone number"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
          />
        </Flex>
      </FormControl>

      <FormControl isRequired>
        <FormLabel>Confirm phone number</FormLabel>
        <Flex gap={2}>
          <Select
            value={countryCode}
            isDisabled
            width="31%"
            aria-label="Country code"
          >
            <option value={countryCode}>{countryCode}</option>
          </Select>
          <Input
            type="tel"
            placeholder="Confirm phone number"
            value={confirmPhoneNumber}
            onChange={(e) => setConfirmPhoneNumber(e.target.value)}
          />
        </Flex>
      </FormControl>
      {phoneError && <Text fontSize="sm" color={ff.danger}>{phoneError}</Text>}

      {/* Locations Component */}
      <Box width="100%" p={5} border="1px solid" borderColor={ff.line} borderRadius="20px">
        <Text fontWeight={600} fontSize="17px" color={ff.ink} mb={1}>Locations<RequiredStar /></Text>
        <Text fontSize="sm" color={ff.text2} mb={3}>
          Add your collaboration locations.
        </Text>
        <LocationsListWithForm
          locations={locations}
          setLocations={setLocations}
          showToasts
        />
      </Box>

      <Button
        type="submit"
        variant="lime"
        width="full"
        onClick={onSubmit}
        isLoading={isLoading}
        loadingText="Creating account..."
      >
        Submit application
      </Button>
    </>
  );
};

export default ApplicationFormStep;
